import { PropsWithoutRef } from "react";
import {
  Drawer,
  Toolbar,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import MenuIcon from "@mui/icons-material/Menu";
import { SIDEBAR_WIDTH } from "../constants";
import { useCanView } from "../hooks/usePermission";

interface Props {
  mobileOpen: boolean;
  handleSidebarToggle: () => void;
}

export default function Sidebar({
  mobileOpen,
  handleSidebarToggle,
}: PropsWithoutRef<Props>) {
  const location = useLocation();
  const navigate = useNavigate();

  // Permission checks for each sidebar item
  const canViewDashboard = useCanView("Dashboard");
  const canViewUsers = useCanView("Manage Users");
  const canViewWorkouts = useCanView("Workout management");
  const canViewPrograms = useCanView("Manage Programs");
  const canViewSubscriptions = useCanView("Subscription");
  const canViewBadges = useCanView("Achievements & Badges");
  const canViewCategories = useCanView("Categories");
  const canViewRevenue = useCanView("Revenue Management");
  const canViewCoupons = useCanView("Discounts and Coupons");
  const canViewNotifications = useCanView("Notification Management");
  const canViewReports = useCanView("Report Analytics");
  const canViewSubAdmin = useCanView("Manage Sub-admin");
  const canViewCommunity = useCanView("Community Moderation");
  const canViewCms = useCanView("Manage CMS Pages");
  const canViewSettings = useCanView("Settings");
  const canViewPromotions = useCanView("Manage Promotions");

  const drawer = (
    <>
      <Toolbar className="sidebar-logo" sx={{ position: "relative" }}>
        <a
          href="/dashboard"
          onClick={(e) => {
            e.preventDefault();
            navigate("/dashboard");
          }}
          className="logo_link"
        >
          <figure>
            <img src="/static/images/tiffxlogo.png" alt="Tiffx Logo" />
          </figure>
        </a>

        <IconButton
          sx={{
            display: "none",
            position: "absolute",
            top: "10px",
            right: "10px",
          }}
          onClick={handleSidebarToggle}
        >
          <MenuIcon />
        </IconButton>
      </Toolbar>

      <List sx={{ flexGrow: 1 }} className="sidebr-lst">
        {canViewDashboard && (
          <Tooltip title="Dashboard" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/dashboard"}
              onClick={() => navigate("/dashboard")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/dashboard_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Dashboard" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewUsers && (
          <Tooltip title="Manage Users" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-users" ||
                location.pathname === "/manage-users/edit" ||
                location.pathname === "/manage-users/detail"
              }
              onClick={() => navigate("/manage-users")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/user_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage Users" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewWorkouts && (
          <Tooltip
            title="Workout management"
            placement="right"
          >
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-workout" ||
                location.pathname === "/manage-workout/edit" ||
                location.pathname === "/manage-workout/detail"
              }
              onClick={() => navigate("/manage-workout")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/user_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Workout management"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewPrograms && (
          <Tooltip title="Manage Programs" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-programs" ||
                location.pathname === "/manage-programs/edit" ||
                location.pathname === "/manage-programs/detail"
              }
              onClick={() => navigate("/manage-programs")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/user_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage Programs" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewSubscriptions && (
          <Tooltip title="Subscription" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/subscription" ||
                location.pathname === "/subscription/edit" ||
                location.pathname === "/subscription/detail"
              }
              onClick={() => navigate("/subscription")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/user_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Subscription" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewBadges && (
          <Tooltip title="Achievements & Badges " placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/achievements"}
              onClick={() => navigate("/achievements")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/rating.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Achievements & Badges "
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewCategories && (
          <Tooltip title="Categories" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-categories" ||
                location.pathname ===
                  "/manage-categories/manage-sub-categories" ||
                location.pathname === "/manage-categories/manage-attributes"
              }
              onClick={() => navigate("/manage-categories")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/user_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Categories" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewRevenue && (
          <Tooltip title="Revenue Management" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-transactions" 
              }
              onClick={() => navigate("/manage-transactions")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/revenue.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Revenue Management" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewCoupons && (
          <Tooltip title="Discounts and Coupons" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/discounts" ||
                location.pathname === "/discounts/add"
              }
              onClick={() => navigate("/discounts")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/offer_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Discounts and Coupons"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewNotifications && (
          <Tooltip title="Notification Management" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-notifications" ||
                location.pathname === "/manage-notifications/add" ||
                location.pathname === "/manage-notifications/detail"
              }
              onClick={() => navigate("/manage-notifications")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/logs_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Notification Management"
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewReports && (
          <Tooltip title="Report Analytics" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/revenue-analytic"}
              onClick={() => navigate("/revenue-analytic")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/report_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Report Analytics" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewSubAdmin && (
          <Tooltip title="Manage Sub-admin" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-subadmin" ||
                location.pathname === "/manage-subadmin/add" ||
                location.pathname === "/manage-subadmin/detail" ||
                location.pathname === "/manage-subadmin/roles" ||
                location.pathname === "/manage-subadmin/roles/add" ||
                location.pathname === "/manage-subadmin/roles/detail"
              }
              onClick={() => navigate("/manage-subadmin")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/user_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage Sub-admin" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewCommunity && (
          <Tooltip title="Community Moderation " placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/Community"}
              onClick={() => navigate("/Community")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/rating.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText
                className="lstitm-txt"
                primary="Community Moderation "
              />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewPromotions && (
          <Tooltip title="Manage Promotions" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={
                location.pathname === "/manage-promotions" ||
                location.pathname === "/manage-promotions/add" ||
                location.pathname === "/manage-promotions/edit" ||
                location.pathname === "/manage-promotions/view"
              }
              onClick={() => navigate("/manage-promotions")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/offer_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage Promotions" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewCms && (
          <Tooltip title="Manage CMS Pages" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/manage-cms"}
              onClick={() => navigate("/manage-cms")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/cms_icon.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Manage CMS Pages" />
            </ListItemButton>
          </Tooltip>
        )}

        {canViewSettings && (
          <Tooltip title="Tags" placement="right">
            <ListItemButton
              className="lst-itm"
              selected={location.pathname === "/settings"}
              onClick={() => navigate("/settings")}
            >
              <ListItemIcon sx={{ minWidth: '22px', maxWidth: '22px' }}>
                <img src="/static/images/settings.svg" alt="Icon" style={{ width: '100%' }} />
              </ListItemIcon>
              <ListItemText className="lstitm-txt" primary="Tags" />
            </ListItemButton>
          </Tooltip>
        )}
      </List>
    </>
  );

  return (
    <Box
      component="nav"
      className="sidebar-main-div"
      sx={{ width: { lg: SIDEBAR_WIDTH }, flexShrink: { lg: 0 } }}
      aria-label="mailbox folders"
    >
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleSidebarToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
          },
        }}
      >
        {drawer}
      </Drawer>
      <Drawer
        variant="permanent"
        sx={{
          display: { xs: "none", lg: "block" },
          "& .MuiDrawer-paper": {
            boxSizing: "border-box",
            width: SIDEBAR_WIDTH,
          },
        }}
        open
      >
        {drawer}
      </Drawer>
    </Box>
  );
}
